import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'Jak dlouho trvá vidět výsledky?',
    answer: 'První výsledky typicky vidíme do 4–6 týdnů. Záleží na stavu webu a konkurenci ve vašem oboru. AI nástroje se učí postupně, takže efekt se zvyšuje v čase.',
  },
  {
    question: 'Co je AI Visibility Audit?',
    answer: 'Audit je komplexní analýza vašeho webu zaměřená na viditelnost v AI nástrojích jako ChatGPT, Perplexity nebo Google SGE. Zjistíme, jak často a v jakém kontextu vás AI cituje, a navrhneme konkrétní úpravy.',
  },
  {
    question: 'Musím měnit celý web?',
    answer: 'Ne. Většinou stačí upravit 5–10 klíčových stránek, které řeší rozhodovací dotazy vašich zákazníků. Zaměříme se na obsah, který má největší dopad.',
  },
  {
    question: 'Jak se liší AI SEO od klasického SEO?',
    answer: 'Klasické SEO cílí na pozice ve vyhledávačích. AI SEO (GEO) cílí na to, aby váš obsah byl použit jako zdroj v AI odpovědích. Vyžaduje jinou strukturu obsahu — krátké úvody, jasná kritéria, FAQ.',
  },
  {
    question: 'Co když AI nástroje změní algoritmus?',
    answer: 'Sledujeme změny v AI nástrojích průběžně a upravujeme strategii. Naše klienti dostávají pravidelné reporty a doporučení podle aktuálního stavu.',
  },
];

const FAQ = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8 relative"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-0 w-[350px] h-[350px] bg-[#00D4AA]/8 rounded-full blur-[200px] -translate-y-1/2 pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#00A8E8]/30 mb-6"
            whileHover={{ scale: 1.03 }}
          >
            <span className="text-sm text-[#00A8E8] font-medium">FAQ</span>
          </motion.div>

          <h2 className="text-headline font-bold mb-6 text-slate-900">
            Často kladené <span className="text-gradient">otázky</span>
          </h2>

          <p className="text-body text-lg">
            Máte otázky? Máme odpovědi.
          </p>
        </motion.div>

        {/* Accordion */}
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="glass-card rounded-2xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left group"
              >
                <span className="text-slate-800 font-medium pr-4 group-hover:text-gradient transition-colors">
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-[#00D4AA] transition-colors" />
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-6 pb-5">
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
